// Centralized Image URL Configuration
// Use local URLs for development, production for build

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
export const IMAGE_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

/**
 * Get full image URL from relative or absolute path
 * @param imagePath - Can be: /uploads/xxx.jpg, http://..., or just filename
 * @returns Full URL that can be used in <img src>
 */
/** Fix legacy wrong paths from upload bug (navigations vs navigation, settings fallback) */
export function normalizeUploadPath(imagePath: string): string {
  let p = imagePath.trim();
  if (!p) return p;
  p = p.replace(/\/uploads\/navigations\//i, '/uploads/navigation/');
  return p;
}

export const getImageUrl = (imagePath: string | undefined | { url?: string }): string => {
  if (!imagePath) return '';
  
  // Handle object with url property (e.g., { url: '...', alt: '...' })
  if (typeof imagePath === 'object') {
    const url = imagePath.url || '';
    if (url && url.startsWith('/uploads')) {
      return `${IMAGE_BASE_URL}${normalizeUploadPath(url)}`;
    }
    return url;
  }
  
  // Already a full URL - return as is (after path fix for our host)
  if (typeof imagePath === 'string' && imagePath.startsWith('http')) {
    try {
      const u = new URL(imagePath);
      if (u.pathname.startsWith('/uploads')) {
        u.pathname = normalizeUploadPath(u.pathname);
        return u.toString();
      }
    } catch {
      /* ignore */
    }
    return imagePath;
  }
  
  // Relative URL starting with /uploads - prepend backend URL
  if (typeof imagePath === 'string' && imagePath.startsWith('/uploads')) {
    return `${IMAGE_BASE_URL}${normalizeUploadPath(imagePath)}`;
  }

  // uploads/... without leading slash
  if (typeof imagePath === 'string' && imagePath.startsWith('uploads/')) {
    return `${IMAGE_BASE_URL}/${normalizeUploadPath(imagePath)}`;
  }
  
  // Just a filename, assume it's in uploads with full URL
  return `${IMAGE_BASE_URL}/uploads/${imagePath}`;
};

// Alias for backward compatibility
export const getFullImageUrl = getImageUrl;

/**
 * Get full banner image URL
 */
export const getBannerImageUrl = (imagePath: string | undefined | { url?: string }): string => {
  if (!imagePath) return '';
  
  // Handle object with url property
  if (typeof imagePath === 'object') {
    const url = imagePath.url || '';
    if (url && url.startsWith('/uploads')) {
      return `${IMAGE_BASE_URL}${url}`;
    }
    return url;
  }
  
  if (typeof imagePath === 'string' && imagePath.startsWith('http')) {
    return imagePath;
  }
  
  if (typeof imagePath === 'string' && imagePath.startsWith('/uploads')) {
    return `${IMAGE_BASE_URL}${imagePath}`;
  }
  
  return `${IMAGE_BASE_URL}/uploads/banners/${imagePath}`;
};

/**
 * Get full product image URL
 */
export const getProductImageUrl = (imagePath: string | undefined | { url?: string }): string => {
  if (!imagePath) return '';
  
  // Handle object with url property
  if (typeof imagePath === 'object') {
    const url = imagePath.url || '';
    if (url && url.startsWith('/uploads')) {
      return `${IMAGE_BASE_URL}${url}`;
    }
    return url;
  }
  
  if (typeof imagePath === 'string' && imagePath.startsWith('http')) {
    return imagePath;
  }
  
  if (typeof imagePath === 'string' && imagePath.startsWith('/uploads')) {
    return `${IMAGE_BASE_URL}${imagePath}`;
  }
  
  return `${IMAGE_BASE_URL}/uploads/products/${imagePath}`;
};
